import { ReactNode, useEffect, useMemo, useState } from "react";
import { CreateLinkProps } from "@tanstack/react-router";
import classnames from "classnames";

import { TextLink, TextLinkProps } from "components/actions/Links";
import { RouterTextLink, RouterTextLinkOptions } from "components/actions/RouterLink";
import Tooltip, { TooltipComponentProps } from "components/ui/tooltip/Tooltip";

import css from "./EllipsisTooltip.module.scss";

type EllipsisTooltipProps = {
  fullText: string;
  tooltipProps?: Omit<TooltipComponentProps, "children" | "content">;
  containerClassname?: string;
  noWrap?: boolean;
};

/**
 * Show some text with ellipsis, and a tooltip revealing the full text on hover, if the ellipsis is
 * active.
 *
 * This is the base component, and then there are some more specific implementations below -
 * the reason being is that it is easier when the type of children are known and to some degree
 * controlled, as opposed to having a fully-flexible generic element.
 */
function EllipsisTooltip({
  fullText,
  tooltipProps,
  containerClassname,
  noWrap,
  children,
}: React.PropsWithChildren<EllipsisTooltipProps>): JSX.Element {
  const [spanElem, setSpanElem] = useState<HTMLSpanElement | null>(null);
  const [hasTooltip, setHasTooltip] = useState(false);
  const resizeObserver = useMemo(() => {
    return new ResizeObserver((entries) => {
      for (const entry of entries) {
        const elem = entry.target as HTMLSpanElement;
        setHasTooltip(elem.offsetWidth < elem.scrollWidth || elem.offsetHeight < elem.scrollHeight);
      }
    });
  }, []);

  useEffect(() => {
    if (spanElem) {
      setHasTooltip(
        spanElem.offsetWidth < spanElem.scrollWidth ||
          spanElem.offsetHeight < spanElem.scrollHeight,
      ); // set initial value
      resizeObserver.observe(spanElem);
      return () => {
        resizeObserver.unobserve(spanElem);
      };
    }
  }, [spanElem, resizeObserver]);

  const content = (
    <span
      className={classnames(css.ellipsis, { [css.noWrap]: noWrap }, containerClassname)}
      ref={(elem) => setSpanElem(elem)}
    >
      {children}
    </span>
  );

  if (hasTooltip) {
    return (
      <Tooltip {...tooltipProps} content={fullText}>
        {content}
      </Tooltip>
    );
  } else {
    return content;
  }
}

/**
 * @deprecated Replace with components/ui/tooltip/EllipsisTooltip.tsx + `<TextLink />` component with `ellipsis` attribute
 */
export function EllipsisTooltipTextLink({
  fullText,
  linkText,
  linkProps,
  tooltipProps,
  noWrap,
  containerClassname,
}: {
  linkProps: TextLinkProps;
  tooltipProps?: EllipsisTooltipProps["tooltipProps"];
  fullText: string;
  linkText: React.ReactNode;
  noWrap?: boolean;
  containerClassname?: string;
}): JSX.Element {
  return (
    <EllipsisTooltip
      fullText={fullText}
      tooltipProps={tooltipProps}
      noWrap={noWrap}
      containerClassname={containerClassname}
    >
      <TextLink {...linkProps}>{linkText}</TextLink>
    </EllipsisTooltip>
  );
}

/**
 * EllipsisTooltip + TanStack Router Link — for use in pages migrated to TanStack Router.
 * Eventually need to deprecate and replace with components/ui/tooltip/EllipsisTooltip.tsx + `<RouterTextLink />` component with `ellipsis` attribute
 */
export function EllipsisTooltipRouterLink({
  fullText,
  linkText,
  linkProps,
  tooltipProps,
  noWrap,
  containerClassname,
}: {
  linkProps: RouterTextLinkOptions;
  tooltipProps?: EllipsisTooltipProps["tooltipProps"];
  fullText: string;
  linkText: ReactNode;
  noWrap?: boolean;
  containerClassname?: string;
}): JSX.Element {
  return (
    <EllipsisTooltip
      fullText={fullText}
      tooltipProps={tooltipProps}
      noWrap={noWrap}
      containerClassname={containerClassname}
    >
      {/* Target validated at the source (appRoutes `satisfies LinkProps`); the
          object can't carry TanStack's inline `to`→`params` generics through a spread. */}
      <RouterTextLink {...(linkProps as unknown as CreateLinkProps)}>{linkText}</RouterTextLink>
    </EllipsisTooltip>
  );
}

/**
 * @deprecated Replace with components/ui/tooltip/EllipsisText.tsx
 */
export function EllipsisTooltipText({
  fullText,
  childText,
  tooltipProps,
  textProps,
  noWrap,
  containerClassname,
}: {
  fullText: string;
  childText?: string;
  tooltipProps?: EllipsisTooltipProps["tooltipProps"];
  textProps?: React.HTMLAttributes<HTMLParagraphElement>;
  noWrap?: boolean;
  containerClassname?: string;
}): JSX.Element {
  return (
    <EllipsisTooltip
      fullText={fullText}
      tooltipProps={tooltipProps}
      noWrap={noWrap}
      containerClassname={containerClassname}
    >
      <p {...textProps} style={{ display: "inline", ...textProps?.style }}>
        {childText ?? fullText}
      </p>
    </EllipsisTooltip>
  );
}
